import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar } from 'lucide-react';
import { contentAPI } from '../utils/api';
import { openReservation } from '../utils/reservations';

const SUBTITLES = {
  // Aligned with the August 2026 physical menu
  'to start': 'small plates and snacks',
  'tandoor & woodfire': 'kebabs and grills from the live fire',
  'classics reimagined': 'regional Indian dishes, reinterpreted',
  'bread': 'fresh from the tandoor',
  'Dessert': 'sweet endings',
  'Cocktails': 'crafted with Indian spices and California spirit',
  'wines by the bottle': 'a curated, low-intervention list — by the bottle',
  'Wines by the Glass': 'a curated, low-intervention list',
  'Beer': 'crisp Indian lagers',
  'Spirit-Free': 'thoughtful non-alcoholic options',
  'indian whisky': 'single malts from Rajasthan, Haryana & Bengaluru',
};

const ACCENT = '#B8451F'; // muted terracotta — the lone accent color

const MenuPage = () => {
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    contentAPI.getMenu().then((data) => {
      if (data && Object.keys(data).length > 0) setMenu(data);
      setLoading(false);
    });
  }, []);

  const menuData = menu || {};
  const sectionNames = Object.keys(menuData);

  return (
    <div className="min-h-screen bg-[#FAFAF7]" style={{ color: '#0a0a0a' }}>
      {/* Minimal top header — no hero image, all type */}
      <section className="pt-28 md:pt-32 pb-12 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <div className="flex justify-between items-baseline text-[10px] md:text-xs tracking-[0.4em] uppercase text-gray-600 mb-4">
            <span>Brick Lane</span>
            <span>À la Carte · 2026</span>
          </div>
          <div className="h-px bg-gray-300 mb-8"></div>
          <Link
            to="/"
            className="text-[10px] md:text-xs tracking-[0.3em] uppercase text-gray-500 hover:text-[color:var(--accent)] transition-colors"
            style={{ '--accent': ACCENT }}
            data-testid="menu-back-link"
          >
            ← Back to Home
          </Link>
        </div>
      </section>

      {/* Menu sections */}
      <section className="px-6 md:px-12 pb-24">
        <div className="max-w-3xl mx-auto">
          {loading && <p className="text-center text-gray-400 py-12 text-sm">Loading menu…</p>}
          {!loading && sectionNames.length === 0 && (
            <p className="text-center text-gray-400 py-12 text-sm">Menu coming soon.</p>
          )}

          {sectionNames.map((section, idx) => (
            <div
              key={section}
              className="mb-10 md:mb-14 last:mb-12"
              data-testid={`menu-section-${section.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            >
              {/* Section header */}
              <div className="mb-6 md:mb-8">
                <h2
                  className="font-light leading-[0.95] tracking-tight lowercase"
                  style={{
                    fontFamily: 'var(--font-serif)',
                    fontWeight: 300,
                    fontSize: 'clamp(2.75rem, 8vw, 5.5rem)',
                  }}
                >
                  {section}
                </h2>
                {SUBTITLES[section] && (
                  <p
                    className="italic text-gray-500 mt-4 md:mt-6 max-w-md text-sm md:text-base leading-relaxed"
                    style={{ fontFamily: 'var(--font-serif)' }}
                  >
                    {SUBTITLES[section]}
                  </p>
                )}
              </div>

              {/* Items */}
              <div className="space-y-10 md:space-y-12">
                {menuData[section].map((item, i) => (
                  <React.Fragment key={`${item.name}-${i}`}>
                    {item.sub_header && (
                      <div className="pt-8 first:pt-0">
                        <div className="flex items-center gap-4">
                          <span
                            className="text-[10px] md:text-xs tracking-[0.4em] uppercase whitespace-nowrap"
                            style={{ color: ACCENT }}
                          >
                            {item.sub_header}
                          </span>
                          <span className="flex-1 h-px bg-gray-300"></span>
                        </div>
                      </div>
                    )}
                    <div data-testid={`menu-item-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                      <div className="flex items-baseline gap-4 mb-2">
                        <h3
                          className="font-normal leading-snug flex-1"
                          style={{
                            fontFamily: 'var(--font-serif)',
                            fontSize: 'clamp(1.125rem, 2.5vw, 1.375rem)',
                            color: '#0a0a0a',
                          }}
                        >
                          {item.name}
                        </h3>
                        {item.price && (
                          <span
                            className="font-normal leading-snug whitespace-nowrap tabular-nums"
                            style={{
                              fontFamily: 'var(--font-serif)',
                              fontSize: 'clamp(1.125rem, 2.5vw, 1.375rem)',
                              color: '#0a0a0a',
                            }}
                            data-testid={`menu-price-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                          >
                            {item.price}
                          </span>
                        )}
                      </div>
                      {item.description && (
                        (item.description.includes('\n\n')) ? (
                          <div
                            className="text-sm md:text-base text-gray-500 leading-relaxed font-light space-y-4 md:space-y-5"
                            data-testid={`menu-desc-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                          >
                            {item.description.split(/\n\n+/).map((block, bi) => {
                              const dashIdx = block.indexOf('—');
                              if (dashIdx > 0 && dashIdx < 40) {
                                const label = block.slice(0, dashIdx).trim();
                                const rest = block.slice(dashIdx + 1).trim();
                                return (
                                  <p key={bi} className="whitespace-pre-line">
                                    <span className="text-[#0a0a0a] font-normal">{label}</span>
                                    <span className="text-gray-400"> — </span>
                                    {rest}
                                  </p>
                                );
                              }
                              return (
                                <p key={bi} className="whitespace-pre-line">{block}</p>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-sm md:text-base text-gray-500 leading-relaxed font-light whitespace-pre-line">
                            {item.description}
                          </p>
                        )
                      )}
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          ))}

          {/* Footer notes */}
          <div className="mt-32 pt-12 border-t border-gray-300">
            <p className="text-xs md:text-sm tracking-[0.3em] uppercase mb-6" style={{ color: ACCENT }}>
              Our kitchen is refined-seed-oil free
            </p>
            <p className="text-xs md:text-sm text-gray-500 leading-relaxed font-light max-w-2xl">
              Many of our dishes can be made vegan — please ask your server for recommendations and modifications.
            </p>
            <p className="text-[11px] md:text-xs text-gray-400 leading-relaxed mt-4 max-w-2xl">
              *Consuming raw or undercooked meats, poultry, seafood, shellfish, or eggs may increase your risk of foodborne illness.
            </p>
          </div>

          {/* Reservations CTA — restrained */}
          <div className="mt-20 mb-8">
            <button
              onClick={() => openReservation(null, 'menu-bottom')}
              className="text-xs md:text-sm tracking-[0.3em] uppercase border-b border-[#0a0a0a] pb-1 hover:opacity-60 transition-opacity inline-flex items-center gap-2"
              style={{ color: '#0a0a0a' }}
              data-testid="menu-reserve-btn"
            >
              <Calendar size={14} /> RESERVE
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MenuPage;
